import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import React, { useCallback, useMemo } from 'react';
import { Layout } from 'tea-component';
import { useLocation } from 'umi';
import useIntls from '@opm/hooks/useIntls';
import useMenuTitle from '@opm/hooks/useMenuTitle';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { CompetitorsAdCreativesPoint } from '@opm/operationLog/pages';
import CompetitorsAdCreativesTable from './CompetitorsAdCreativesTable';
import MarketContentBody from '@opm/components/MarketContentBody';
import ScrollBottomList from '@opm/components/ScrollBottomList';
import { adCreativesProcessor } from './processors';
import './index.scss';
import { DBO_TM } from '@op/utils/format';
import { TranslateConfigProvider } from '@opm/hooks/useTranslateConfig';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import BubbleCommon from '@op/components/BubbleCommon';
import { adCreativesHeaderTipsMap, adCreativesStopTipsMap } from '@opm/components/BubbleCommon';
import { AI_TAGS_COLORS } from '@op/pages/community/video/components/TagCard';
import { MARKETING_AI_TAG_COLOR_KEY } from './constants';
import marketConnect from '@opm/components/MarketConnect';
import {
  dealwithMaterialsList,
  IMaterialsListDefaultFilter,
  MATERIALS_LIST_API,
  TByGameGenre,
} from '@opm/components/MarketConnect/utils/materialsList';
import { useGenreDateAutoTime } from '@opm/hooks/useGenreDateAutoTime';

const { Content } = Layout;

const fallbackDefaultFilter = {
  ...DBO_TM.filterRangeDay(6, 0),
  date_type: 'daily' as const,
};

interface ICompetitorsAdCreativesProps {
  defaultFilter?: IMaterialsListDefaultFilter;
  byGameGenre?: TByGameGenre;
}

function CompetitorsAdCreatives({ defaultFilter, byGameGenre }: ICompetitorsAdCreativesProps) {
  const location = useLocation();
  const stateParams = (location.state || {}) as Record<string, any>;
  const mergedDefault = useMemo(
    () => ({ ...fallbackDefaultFilter, ...defaultFilter, ...stateParams }),
    [defaultFilter, stateParams],
  );

  DBOM_POINT.useInitPage({ buriedPoint: CompetitorsAdCreativesPoint });
  const { currentTitle } = useMenuTitle();
  const { intl } = useIntls();
  const { filterRef, filter, setFilter, loading, ...filterProps } =
    useFilterCommon({
      keys: [
        'ADGame',
        'ADMultiDateRange',
        'ADCountryRegion',
        'ADChannel',
        'ADType',
        'ADPlatform',
        'ADImpressions',
        'ADActiveDays',
        'ADFirstSeenDateSelect',
        'ADLastSeenDateSelect',
      ],
    });

  // 品类切换自动更新时间
  const { onGenreChange } = useGenreDateAutoTime({
    byGameGenre,
    dayRange: 6,
    filterRef,
    filter,
  });

  const filterConfigProps = useMemo(
    () => ({ ADGame: { onGenreChange } }),
    [onGenreChange],
  );

  /** cond 回调：从 materials_config_video_tags 构建颜色映射写入 sessionStorage */
  const handleCondConfig = useCallback((condData: any) => {
    const videoTags = condData?.materials_config_video_tags;
    if (!Array.isArray(videoTags) || videoTags.length === 0) return;
    try {
      const colorMap: any = {};
      videoTags.forEach((item: any, index: number) => {
        colorMap[item.label_en] = AI_TAGS_COLORS[index];
      });
      sessionStorage.setItem(MARKETING_AI_TAG_COLOR_KEY, JSON.stringify(colorMap));
    } catch (e) {
      console.warn('Failed to save AI_TAG_COLOR:', e);
    }
  }, []);

  return (
    <TranslateConfigProvider processor={adCreativesProcessor}>
      <ScrollBottomList>
        <Content className="dbom-competitors-ad-creatives-page">
          <Content.Header
            title={currentTitle}
            subtitle={
              <div className="dbo-header-subtitle">
                {intl({ id: 'Data Update' })}
                <BubbleCommon showIcon={false} tipsMap={adCreativesHeaderTipsMap}>
                  <TipContext className="title-tip" placement="bottom" />
                </BubbleCommon>
              </div>
            }
          />
          <MarketContentBody
            loading={loading}
            horseRaceLampTips={adCreativesStopTipsMap}
            filterSlot={
              <FilterCommon
                {...filterProps}
                userDefaultValue={mergedDefault}
                loadingHeight={150}
                useOriDefaultValue
                realTimeMode
                filterConfigProps={filterConfigProps}
                extrRequestKeys={['materials_config_video_tags']}
                backCondConfig={handleCondConfig}
                confirm={(value: any) => {
                  setFilter(value);
                }}
              />
            }
          >
            <CompetitorsAdCreativesTable filter={filter} verticalCard defaultView="card" />
          </MarketContentBody>
        </Content>
      </ScrollBottomList>
    </TranslateConfigProvider>
  );
}

export default marketConnect<ICompetitorsAdCreativesProps>({
  ids: [MATERIALS_LIST_API],
  dealwith: dealwithMaterialsList(MATERIALS_LIST_API),
})(CompetitorsAdCreatives);
